import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchPublicDirectory } from "@/lib/api/users";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Users, ExternalLink } from "lucide-react";
import { useDeferredValue } from "react";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton } from "@/components/loading";
import { usePaginationState } from "@/hooks/use-pagination-state";
import { PaginationControls } from "@/components/PaginationControls";

// @ts-ignore
export const Route = createFileRoute("/_authenticated/directory")({
  component: DirectoryPage,
});

function DirectoryPage() {
  const { pageNumber, pageSize, search, setPageNumber, setPageSize, setSearch } =
    usePaginationState({ initialPageSize: 21 });
  const deferredSearch = useDeferredValue(search);

  // The query is keyed on the deferred search value, but page/size resets to 1
  // happen synchronously in setSearch — the deferred value trailing behind for a
  // render or two is fine since the query key still includes pageNumber/pageSize.
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["directory", deferredSearch, pageNumber, pageSize],
    queryFn: () => fetchPublicDirectory(deferredSearch, pageNumber, pageSize),
  });

  const profiles = data?.items ?? [];

  return (
    <div className="space-y-6">
      {/* Header section with entrance animation */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-background border p-6 md:p-8 animate-in fade-in slide-in-from-top-4 duration-500">
        <div className="relative z-10 max-w-2xl">
          <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
            Community Directory
          </h1>
          <p className="mt-2 text-muted-foreground text-sm md:text-base leading-relaxed">
            Discover and connect with approved students and peers in our learning community.
          </p>
          <div className="relative mt-6 max-w-md">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name..."
              className="pl-10 bg-background text-foreground border shadow-sm transition-all duration-300 focus-visible:ring-2 focus-visible:ring-primary/30"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        {/* Background glow decorator with subtle breathing pulse */}
        <div
          className="absolute right-0 top-0 -mr-16 -mt-16 h-64 w-64 rounded-full bg-primary/5 blur-3xl animate-pulse"
          style={{ animationDuration: "6s" }}
        />
      </div>

      <div className="w-full">
        {isLoading ? (
          <CardSkeleton count={6} className="md:grid-cols-2 lg:grid-cols-3" />
        ) : profiles.length === 0 ? (
          <div className="text-center py-12 border rounded-xl bg-muted/10 animate-in fade-in duration-300">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium">No profiles found</h3>
            <p className="text-muted-foreground">Try adjusting your search criteria.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {profiles.map((profile: any, index: number) => (
                <Card
                  key={profile.id}
                  className="hover:border-primary/45 hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex flex-col animate-in fade-in slide-in-from-bottom-4"
                  style={{ animationDelay: `${index * 50}ms`, animationFillMode: "both" }}
                >
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-14 w-14 border transition-transform duration-300 hover:scale-105">
                        <AvatarImage src={profile.avatar_url || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {profile.full_name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3
                          className="font-semibold text-lg truncate group-hover:text-primary transition-colors"
                          title={profile.full_name || ""}
                        >
                          {profile.full_name}
                        </h3>
                        <p
                          className="text-sm text-muted-foreground truncate"
                          title={profile.study || ""}
                        >
                          {profile.study || "Student"}
                        </p>
                        {profile.batch && (
                          <Badge
                            variant="secondary"
                            className="mt-2 text-[10px] uppercase font-semibold"
                          >
                            Batch {profile.batch}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {profile.bio && (
                      <p className="text-sm mt-4 text-muted-foreground line-clamp-3 leading-relaxed">
                        {profile.bio}
                      </p>
                    )}

                    <div className="mt-auto pt-6">
                      <a
                        href={`/p/${profile.public_slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center text-sm font-medium text-primary hover:underline group"
                      >
                        View Portfolio
                        <ExternalLink className="ml-1 h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <PaginationControls
              pageNumber={data?.pageNumber ?? pageNumber}
              pageSize={data?.pageSize ?? pageSize}
              totalPages={data?.totalPages ?? 0}
              totalCount={data?.totalCount ?? 0}
              hasNextPage={data?.hasNextPage ?? false}
              hasPreviousPage={data?.hasPreviousPage ?? false}
              onPageChange={setPageNumber}
              onPageSizeChange={setPageSize}
              isLoading={isFetching}
            />
          </>
        )}
      </div>
    </div>
  );
}
